// ... (imports and initial setup unchanged)
import { useCallback, useEffect, useRef, useState } from "react";
import "../../styles/UserHome.css";
import "../../styles/Form.css";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { FaCheck } from "react-icons/fa";
import DonutFileStatusChart from "../../components/DonutFileStatusChart";
import { fetchLatestTaskStatus } from "../../utils/taskStatus";

const DEFAULT_FORM = {
  first_name: "",
  mid_name: "",
  last_name: "",
  email: "",
  mobile: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  country: "",
  postal_code: "",
  dob: "",
  profilePic: "",
  userid_no: "",
  gender: "",
  sin: "",
  user_hiring_date: "",
};

function getStoredCurrentUser(isOwnHome) {
  if (!isOwnHome) return null;

  const raw =
    localStorage.getItem("me") ||
    localStorage.getItem("user") ||
    localStorage.getItem("currentUser");

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn("Failed to parse cached user profile:", error);
    return null;
  }
}

function UserHome() {
  const HIDDEN_HOME_TASK_KEYS = new Set([
    "backgroundcheck",
    "background_check_fcra",
    "consent_history",
    "psp_disclosure_and_authorization",
    "summary_of_rights_under_fcra",
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const loggedInUserId = localStorage.getItem("userId");
  const userId = id || loggedInUserId;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const currentUserId = Number(loggedInUserId);
  const isAdmin = localStorage.getItem("userRole") === "1";
  const isOwnHome = !id || Number(id) === currentUserId;

  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...(getStoredCurrentUser(isOwnHome) || {}),
  }));

  const [selectedTraining, setSelectedTraining] = useState("");
  const [taskStatus, setTaskStatus] = useState({});
  const [categories, setCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [assignedTrainings, setAssignedTrainings] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]); // [{category:{id,name}, tasks:[...] }]
  const [showSin, setShowSin] = useState(false);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const profileUploadInputRef = useRef(null);
  const placeholderLabel = isAdmin ? "Upload Image" : "User";

  const formatSin = (sin, show) => {
    if (!sin) return "-";
    if (show) return sin;

    const last4 = sin.slice(-4);
    return `*****${last4}`;
  };

  const handleProfileUpload = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      alert("Please select an image file.");
      event.target.value = "";
      return;
    }

    try {
      setIsUploadingProfile(true);

      const formData = new FormData();
      formData.append("profilePic", selectedFile);
      formData.append("userId", userId);

      const res = await axios.put(
        `${BASE_URL}/api/users/${userId}/profile-picture?userId=${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const updatedProfilePic = res.data?.profilePic || "";
      setForm((prev) => ({ ...prev, profilePic: updatedProfilePic }));

      if (isOwnHome) {
        const updatedUser = { ...form, profilePic: updatedProfilePic };
        localStorage.setItem("me", JSON.stringify(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error("Failed to upload profile picture.", error);
      alert("Failed to upload profile picture.");
    } finally {
      setIsUploadingProfile(false);
      event.target.value = "";
    }
  };

  const handleTrainingSelect = (e) => {
    const value = e.target.value;
    setSelectedTraining(value);
    if (!value) return;

    // 🔹 Internal training
    navigate(`/training/${userId}`, {
      state: {
        openModuleKey: value,
      },
    });
  };

  const hasProfileImage = Boolean(form.profilePic);
  const showProfileImage = hasProfileImage && !profileImageBroken;
  const profileImageUrl = hasProfileImage
    ? `${BASE_URL}/uploads/profilepic/${form.profilePic}`
    : null;

  useEffect(() => {
    setProfileImageBroken(false);
  }, [form.profilePic]);

  const loadAssignedTasks = useCallback(async () => {
    if (!userId) return;

    const res = await axios.get(`${BASE_URL}/api/tasks/assigned/${userId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      params: { _: Date.now() },
    });
    const rows = res.data?.tasks || [];

    // Group by category
    const byCat = new Map();
    rows.forEach((r) => {
      const k = r.t_category_id;
      if (!byCat.has(k)) {
        byCat.set(k, {
          category: {
            id: r.t_category_id,
            name: r.tc_name,
            key: r.tc_key, // e.g. 'employment_record', 'compliance'
            order: r.tc_order ?? 9999,
          },
          tasks: [],
        });
      }
      byCat.get(k).tasks.push({ ...r, order: r.t_order ?? 9999 });
    });

    // 1) sort tasks within each category
    for (const g of byCat.values()) {
      g.tasks.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    }

    // 2) force Employment Record first, Compliance second, then tc_order
    const PRIORITY = { employment_record: 0, compliance: 1 };
    const sortedGroups = Array.from(byCat.values()).sort((a, b) => {
      const pa = PRIORITY[a.category.key] ?? 100;
      const pb = PRIORITY[b.category.key] ?? 100;
      if (pa !== pb) return pa - pb;
      if ((a.category.order ?? 9999) !== (b.category.order ?? 9999)) {
        return (a.category.order ?? 9999) - (b.category.order ?? 9999);
      }
      return String(a.category.name).localeCompare(String(b.category.name));
    });

    setAssignedGroups(sortedGroups);
  }, [userId, BASE_URL]);

  useEffect(() => {
    loadAssignedTasks().catch(console.error);
  }, [loadAssignedTasks, location.key]);

  // 1) bring back the profile fetch so setForm is used
  useEffect(() => {
    if (!userId) return;

    const token = localStorage.getItem("token");
    const cachedUser = getStoredCurrentUser(isOwnHome);

    if (cachedUser) {
      setForm((prev) => ({ ...prev, ...cachedUser }));
    }

    const loadProfile = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setForm({ ...DEFAULT_FORM, ...res.data });

        if (isOwnHome) {
          localStorage.setItem("me", JSON.stringify(res.data));
          localStorage.setItem("user", JSON.stringify(res.data));
        }
      } catch (error) {
        if (!isOwnHome) {
          console.error("Failed to load user profile.", error);
          return;
        }

        try {
          const res = await axios.get(`${BASE_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setForm((prev) => ({ ...prev, ...res.data }));
        } catch (fallbackError) {
          console.error("Failed to load user home profile.", fallbackError);
        }
      }
    };

    loadProfile();
  }, [userId, BASE_URL, isOwnHome]);

  // 2) define openTask (you call it in the onClick)
  const openTask = (task) => {
    const viewingUserId = userId; // target user

    if (task.t_key === "consent_history") {
      // ✅ go to list to choose which employer (regular flow, not slide)
      navigate(`/consent/list/${viewingUserId}`);
      return;
    }

    // your existing special case for user_information (profile vs edit)
    if (task.t_key === "user_information") {
      if (isAdmin && currentUserId !== Number(viewingUserId)) {
        navigate(`/edit-user/${viewingUserId}`);
      } else {
        navigate(`/profile`);
      }
      return;
    }

    // default
    if (!task.t_route) return;
    navigate(task.t_route.replace(":userId", viewingUserId));
  };

  const getTaskStatus = (task) => {
    const idKey = String(task.t_id);
    const keyKey = String(task.t_key || "");

    // 1️⃣ direct match (non-reference tasks)
    let status = taskStatus?.[idKey] || taskStatus?.[keyKey] || "not_completed";

    // 2️⃣ reference-based tasks (Work Permit, ADC, Driver Disclosure)
    if (task.t_requires_ref || Number(task.t_show_list) === 1) {
      const refKeys = Object.keys(taskStatus).filter((k) =>
        k.startsWith(`${idKey}_`),
      );

      if (refKeys.length) {
        // pick latest reference id
        const latestKey = refKeys
          .map((k) => ({
            key: k,
            ref: Number(k.split("_")[1]),
          }))
          .filter((x) => Number.isFinite(x.ref))
          .sort((a, b) => b.ref - a.ref)[0];

        if (latestKey?.key && taskStatus[latestKey.key]) {
          status = taskStatus[latestKey.key];
        }
      }
    }

    return status;
  };

  const isDone = (task) => getTaskStatus(task) === "completed";
  const isReview = (task) => getTaskStatus(task) === "review";
  const isAlmostExpired = (task) => getTaskStatus(task) === "almost_expired";

  // Categories that don't require signing shouldn't be counted in Task Status
  const EXCLUDE_KEYS = new Set(["employment_record", "compliance"]);
  // flatten assigned tasks but skip excluded categories
  const assignedTaskList = assignedGroups.flatMap((g) =>
    EXCLUDE_KEYS.has(String(g.category.key || "").toLowerCase())
      ? []
      : g.tasks.filter(
          (task) =>
            !HIDDEN_HOME_TASK_KEYS.has(String(task.t_key || "").toLowerCase()),
        ),
  );

  // DB-driven totals
  const assignedTotal = assignedTaskList.length;
  const completedCount = assignedTaskList.filter((t) => isDone(t)).length;
  const almostExpiredCount = assignedTaskList.filter((t) => isAlmostExpired(t)).length;
  const reviewCount = assignedTaskList.filter((t) => isReview(t)).length;
  const notStartedCount = Math.max(
    assignedTotal - completedCount - almostExpiredCount - reviewCount,
    0,
  );

  const fetchTaskStatus = useCallback(async () => {
    if (!userId) return;

      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const token = localStorage.getItem("token");
        const baseStatus = res.data.status || {};

        // Vacation
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/vacationform/latest/status",
          taskId: 1,
          taskKey: "vacation_form",
          token,
          baseStatus,
        });

        // CVSA
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/cvsa/latest/status",
          taskId: 36,
          taskKey: "cvsa_inspections",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverlicense/latest/status",
          taskId: 22,
          taskKey: "driver_license",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverabstract/latest/status",
          taskId: 80,
          taskKey: "driver_abstract",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driver-pay-rate-agreement/latest/status",
          taskId: 70,
          taskKey: "driver_pay_rate_agreement",
          token,
          baseStatus,
        });

        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driverlicense/latest/status",
          taskId: 22,
          taskKey: "driver_license",
          token,
          baseStatus,
        });

        // Annual Driver Certification ✅
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/annual-driver-certifications/latest/status",
          taskId: 57,
          taskKey: "annual_driver_certification",
          token,
          baseStatus,
        });

        // Driver Disclosure ✅
        await fetchLatestTaskStatus({
          baseUrl: BASE_URL,
          userId,
          endpoint: "/api/driver-disclosures/latest/status",
          taskId: 58,
          taskKey: "driver_disclosure",
          token,
          baseStatus,
        });

        // Existing Consent History logic...
        const employersRes = await axios.get(
          `${BASE_URL}/api/work-history/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const employers = employersRes.data || [];
        const workingEmployers = employers.filter(
          (emp) => emp.wh_type === "Working",
        );
        const requiredConsentTasks = workingEmployers.map(
          (emp) => `consent_history_${emp.wh_id}`,
        );
        if (requiredConsentTasks.length > 0) {
          if (
            requiredConsentTasks.every(
              (task) => baseStatus[task] === "completed",
            )
          ) {
            baseStatus["consent_history"] = "completed";
          } else if (
            requiredConsentTasks.every((task) => baseStatus[task] === "review")
          ) {
            baseStatus["consent_history"] = "review";
          } else {
            baseStatus["consent_history"] = "not_completed";
          }
        }

        setTaskStatus(baseStatus);
      } catch (error) {
        console.error("Failed to fetch task statuses or employer list", error);
      }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus, location.key]);

  useEffect(() => {
    const refreshHomeData = () => {
      loadAssignedTasks().catch(console.error);
      fetchTaskStatus();
    };

    window.addEventListener("focus", refreshHomeData);
    window.addEventListener("pageshow", refreshHomeData);

    return () => {
      window.removeEventListener("focus", refreshHomeData);
      window.removeEventListener("pageshow", refreshHomeData);
    };
  }, [fetchTaskStatus, loadAssignedTasks]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setCategories(res.data))
      .catch((err) => console.error("❌ Failed to fetch categories:", err));
  }, [BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/org-units`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setOrgUnits(res.data))
      .catch((err) => console.error("❌ Failed to fetch Org Units:", err));
  }, [BASE_URL]);

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(`${BASE_URL}/api/trainings/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const internal = res.data.internalModules || [];

        setAssignedTrainings(internal);
      } catch (e) {
        console.error("Failed to load assigned trainings", e);
      }
    };

    load();
  }, [userId, BASE_URL]);

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.c_id === parseInt(categoryId));
    return cat ? cat.c_name : categoryId; // fallback to ID if not found
  };

  const getOrgUnitName = (orgUnitId) => {
    const ou = orgUnits.find((ou) => ou.ou_id === parseInt(orgUnitId));
    return ou ? ou.ou_name : orgUnitId; // fallback to ID if not found
  };

  const fullName = [form.first_name, form.mid_name, form.last_name]
    .filter(Boolean)
    .join(" ");
  const fullAddress = [
    form.address1,
    form.address2,
    form.city,
    form.province,
    form.country,
    form.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
  const formattedDOB = form.dob ? dayjs(form.dob).format("YYYY-MM-DD") : "N/A";
  const age = form.dob ? dayjs().diff(dayjs(form.dob), "year") : "N/A";
  const formattedHiringDate = form.user_hiring_date
    ? dayjs(form.user_hiring_date).format("YYYY-MM-DD")
    : "-";

  return (
    <div className="container-fluid px-2">
      <div className="form-wrapper my-4">
        {/* ✅ Only Admins see this button */}
        {isAdmin && (
          <div className="userhome-topbar">
            <button
              className="status-history-btn d-flex align-items-center gap-2"
              onClick={() => navigate(`/status-history/${userId}`)}
            >
              <i className="fa fa-clock"></i>
              <span>Status History</span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="d-flex align-items-center p-3 bg-white rounded shadow mb-4">
          <div className="user-avatar-wrap me-4">
            {isAdmin ? (
              <button
                type="button"
                className="user-avatar-trigger"
                onClick={() => profileUploadInputRef.current?.click()}
                disabled={isUploadingProfile}
                title="Upload profile picture"
              >
                <span className={`user-avatar-circle ${!showProfileImage ? "is-empty" : ""}`}>
                  {showProfileImage ? (
                    <img
                      src={profileImageUrl}
                      alt="User"
                      className="user-img"
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    <span className="user-avatar-placeholder">
                      {isUploadingProfile ? "Uploading..." : placeholderLabel}
                    </span>
                  )}
                </span>
              </button>
            ) : (
              <div className="user-avatar-trigger">
                <span className={`user-avatar-circle ${!showProfileImage ? "is-empty" : ""}`}>
                  {showProfileImage ? (
                    <img
                      src={profileImageUrl}
                      alt="User"
                      className="user-img"
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    <span className="user-avatar-placeholder">{placeholderLabel}</span>
                  )}
                </span>
              </div>
            )}
            <input
              ref={profileUploadInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handleProfileUpload}
            />
          </div>
          <div>
            <h5>{fullName || "User Name"}</h5>
            <p className="mb-1">
              📞{" "}
              {form.mobile ? (
                <a href={`tel:${form.mobile}`}>{form.mobile}</a>
              ) : (
                "N/A"
              )}{" "}
              • 📧{" "}
              {form.email ? (
                <a href={`mailto:${form.email}`}>{form.email}</a>
              ) : (
                "N/A"
              )}
            </p>
            <p className="mb-0">🏠 {fullAddress || "-"}</p>
          </div>
          <div className="ms-auto text-end d-flex flex-column align-items-end gap-2">
            <div className="mt-2 text-start">
              <p className="mb-2">
                Associate | {getCategoryName(form.emp_category)} |{" "}
                {getOrgUnitName(form.org_unit)}{" "}
              </p>
              <p className="mb-2">
                <strong>Employee ID:</strong> {form.userid_no}
              </p>
              <p className="mb-2">
                <strong>Hiring Date:</strong> {formattedHiringDate}
              </p>
              {isAdmin && (
                <p className="mb-0 d-flex align-items-center gap-2">
                  <strong>SSN:</strong>

                  <span>{formatSin(form.sin, showSin)}</span>

                  {form.sin && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowSin((prev) => !prev)}
                      style={{ padding: "2px 6px" }}
                      title={showSin ? "Hide SSN" : "Show SSN"}
                    >
                      <i
                        className={`fa ${showSin ? "fa-eye-slash" : "fa-eye"}`}
                      />
                    </button>
                  )}
                </p>
              )}
              <p className="mb-0">
                <strong>Birth Date:</strong> {formattedDOB} |{" "}
                <strong>Age:</strong> {age}
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-md-3">
            <div className="bg-white p-3 rounded shadow mb-4">
              <div className="video-card">
                <img
                  src="/videos/homepage.jpg"
                  alt="New Way Transport"
                  className="home-hero-image"
                />
              </div>
            </div>
            <div className="bg-white p-3 rounded shadow mb-4">
              <h6>My Training</h6>

              <select
                className="form-select"
                value={selectedTraining}
                onChange={handleTrainingSelect}
              >
                <option value="">Choose a module to begin...</option>

                {/* 🔹 Internal Trainings */}
                {assignedTrainings.length > 0 && (
                  <>
                    <option disabled>──────── Training ────────</option>
                    {assignedTrainings.map((t) => (
                      <option key={t.tm_id} value={t.tm_key}>
                        - {t.tm_title}
                      </option>
                    ))}
                  </>
                )}

              </select>
            </div>

            <div className="bg-white p-3 rounded shadow mb-4">
              <h6>Self Service</h6>
              <select
                className="form-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) navigate(value);
                }}
              >
                <option value="">Update My...</option>
                <option value={`/driverlicense/${userId}`}>
                  Driver's Licence
                </option>
                <option value={`/driverabstract/${userId}`}>
                  Driver's Abstract
                </option>
                <option value={`/directdeposit/${userId}`}>
                  Direct Deposit Authorization
                </option>
              </select>
            </div>
            <div className="bg-white p-3 rounded shadow text-center file-status-card">
              <h6 className="fs-title"> Task Status</h6>

              <div className="chart-wrapper">
                <DonutFileStatusChart
                  completed={completedCount}
                  almostExpired={almostExpiredCount}
                  review={reviewCount}
                  notStarted={notStartedCount}
                />
              </div>

              <p className="mt-3 mb-0">
                <strong>Tasks:</strong> {assignedTotal}
              </p>

              <div className="status-counts mt-1">
                <span className="badge-complete">
                  ✔️ Complete: {completedCount}
                </span>
                <span className="badge-not-started">
                  🔵 Almost Expired: {almostExpiredCount}
                </span>
                <span className="badge-not-started">
                  🔴 Not Started: {notStartedCount}
                </span>
                <span className="badge-review">
                  ⚠️ Ready for Review: {reviewCount}
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-9">
            <div className="row g-3">
              {assignedGroups.map((group) => (
                <div className="col-md-6" key={group.category.id}>
                  <div className="bg-white p-3 rounded shadow h-100">
                    <h6 className="section-title">{group.category.name}</h6>
                    <hr className="section-divider" />
                    {group.tasks
                      .filter(
                        (t) =>
                          !HIDDEN_HOME_TASK_KEYS.has(
                            String(t.t_key || "").toLowerCase(),
                          ),
                      )
                      .map((t) => {
                        const hideIcons =
                          ["employment_record", "compliance"].includes(
                            String(group.category.key || "").toLowerCase(),
                          ) &&
                          t.t_key !== "cvsa_inspections" &&
                          t.t_key !== "vacation_form";

                        return (
                          <div
                            key={t.t_id}
                            className="hover-item"
                            onClick={() => openTask(t)}
                          >
                            - {t.t_title}{" "}
                            {!hideIcons &&
                              (isDone(t) ? (
                                <span className="ms-2">
                                  <FaCheck color="#198754" />
                                </span>
                            ) : isAlmostExpired(t) ? (
                              <span className="ms-2" title="Almost Expired">🔵</span>
                            ) : isReview(t) ? (
                                <span className="text-warning ms-2">⚠️</span>
                              ) : (
                                <span className="text-danger ms-2">🔴</span>
                              ))}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserHome;

